import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { UserRole } from '../../../components/common/header/photo/head-bar/newsComponent/utils/userRoleHelper';
import { Translate } from '../../../shared/LanguageService';
import NewsFeedArticle from '../feed/NewsFeedArticle';
import { translateNewsFeedLabels } from '../feed/newsFeedLabels';
import { newsPaneWidthPx } from '../feed/newsPaneStyle';
import { getPreviewAudience, PreviewAudienceInput } from './newsPreviewAudience';
import { describeAudience } from './describeAudience';

export type NewsPreview = Pick<
    PreviewAudienceInput,
    'recipientRoles' | 'startDate' | 'endDate' | 'brokerChainCount'
> & {
    visibleDate: string | null;
    title: string;
    content: string;
};

type RoleOption = { label: string; value: UserRole };

type NewsPreviewDialogProps = {
    open: boolean;
    onClose: () => void;
    roles: RoleOption[];
    preview: NewsPreview | null;
};

function PreviewContent({ roles, preview }: { roles: RoleOption[]; preview: NewsPreview }) {
    const [previewRole, setPreviewRole] = React.useState<UserRole>(() =>
        preview.recipientRoles.length > 0 ? preview.recipientRoles[0] : roles[0].value
    );

    const audience = getPreviewAudience({ ...preview, previewRole });
    const shownDate = preview.visibleDate || preview.startDate || '';
    const previewAsLabel = Translate('news-editor-preview/preview-as');

    return (
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                <InputLabel id="news-preview-role-label">{previewAsLabel}</InputLabel>
                <Select
                    labelId="news-preview-role-label"
                    label={previewAsLabel}
                    value={previewRole}
                    onChange={(event) => setPreviewRole(event.target.value as UserRole)}
                >
                    {roles.map((role) => (
                        <MenuItem key={role.value} value={role.value}>
                            {role.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Alert severity={audience.kind === 'shown' ? 'info' : 'warning'}>
                {describeAudience(audience).map((line) => (
                    <div key={line}>{line}</div>
                ))}
            </Alert>

            {audience.kind === 'not-recipient' ? null : (
                <Paper
                    variant="outlined"
                    sx={{ width: '100%', maxWidth: `${newsPaneWidthPx}px`, alignSelf: 'center' }}
                >
                    <Typography
                        component="h2"
                        sx={{
                            padding: '12px 20px',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            color: '#454545'
                        }}
                    >
                        {Translate('header/nav/news-component/title')}
                    </Typography>
                    <Divider />
                    <NewsFeedArticle
                        item={{
                            id: 0,
                            title: preview.title,
                            content: preview.content,
                            date: shownDate,
                            isRead: false
                        }}
                        labels={translateNewsFeedLabels()}
                    />
                </Paper>
            )}
        </DialogContent>
    );
}

export default function NewsPreviewDialog({
    open,
    onClose,
    roles,
    preview
}: NewsPreviewDialogProps) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 600 }}>
                {Translate('news-editor-preview/title')}
            </DialogTitle>
            <Divider />
            {preview && roles.length > 0 ? (
                <PreviewContent roles={roles} preview={preview} />
            ) : null}
            <DialogActions>
                <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: 0 }}>
                    {Translate('news-editor-preview/close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
